import { useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'motion/react'
import { Camera, Images } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface MediaPickerSheetProps {
  open: boolean
  onClose: () => void
  onFileSelected: (file: File) => void
  capture?: 'user' | 'environment'
}

export function MediaPickerSheet({ open, onClose, onFileSelected, capture = 'environment' }: MediaPickerSheetProps) {
  const { t } = useTranslation()
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    onClose()
    onFileSelected(file)
  }

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 20px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'start',
    borderBottom: '1px solid var(--border)',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--fs-body)',
    color: 'var(--text-strong)',
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 200,
              background: 'rgba(8,10,15,0.52)',
              backdropFilter: 'blur(2px)',
            }}
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            style={{
              position: 'fixed',
              insetInlineStart: 0,
              insetInlineEnd: 0,
              bottom: 0,
              zIndex: 201,
              background: 'var(--surface)',
              borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              boxShadow: 'var(--shadow-modal)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            {/* Handle bar */}
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: 'var(--border-strong)',
                margin: '10px auto 8px',
              }}
            />

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture={capture}
              style={{ display: 'none' }}
              onChange={handleChange}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleChange}
            />

            <button type="button" style={rowStyle} onClick={() => cameraInputRef.current?.click()}>
              <Camera size={22} style={{ color: 'var(--brand)', flexShrink: 0 }} />
              {t('common.take_photo')}
            </button>
            <button
              type="button"
              style={{ ...rowStyle, borderBottom: 'none' }}
              onClick={() => galleryInputRef.current?.click()}
            >
              <Images size={22} style={{ color: 'var(--brand)', flexShrink: 0 }} />
              {t('common.choose_gallery')}
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  )
}
